import { Flex, HStack } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef } from 'react';
import { ErrorBoundary } from '../../monitoring/error-boundary';
import { Panel } from '../../panels/panel';
import { PanelTitle } from '../../panels/panel-title';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { DexNotificationsDismisser } from './dex-notifications-dismisser';
import { DexResultsLeftPane } from './dex-results-left-pane';
import { DexResultsMoreActionsMenu } from './dex-results-more-actions-menu';
import { DexResultsRightPane } from './dex-results-right-pane';
import { useDexResults } from './use-dex-results';
import { useDexResultsKeyboardNavigationAndScroll } from './use-dex-results-keyboard-navigation';
import { useSelectDexResults } from './use-select-dex-results';
import { DexNextResultsPanel } from './dex-next-results-panel';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { getUniversalSearchLabel } from '../../common/helpers/get-usx-labels';

type DexResultsPanelBodyParams = {
    leftPanelListContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    rightPanelScrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
};
const DexResultsPanelBody = observer(function DexResultsPanelBody({
    leftPanelListContainerRef,
    rightPanelScrollContainerRef,
}: DexResultsPanelBodyParams): React.ReactElement {
    return (
        <HStack
            gap="0px"
            flexGrow={1}
            overflow="hidden"
            borderBottomLeftRadius="inherit"
            borderBottomRightRadius="inherit"
        >
            <ErrorBoundary name="DEX_RESULTS_LEFT_PANE">
                <DexResultsLeftPane
                    testId="DEX_RESULTS_LEFT_PANE"
                    listContainerRef={leftPanelListContainerRef}
                />
            </ErrorBoundary>
            <ErrorBoundary name="DEX_RESULTS_RIGHT_PANE">
                <DexResultsRightPane
                    testId="DEX_RESULTS_RIGHT_PANE"
                    scrollContainerRef={rightPanelScrollContainerRef}
                />
            </ErrorBoundary>
            <DexNotificationsDismisser />
        </HStack>
    );
});

const DexResultsPanelHeader = observer(function DexResultsPanelHeader(): React.ReactElement {
    const { dexResultsStore } = useDexResults();
    const { dexNextEnabled } = useEnabledFeatures();

    return (
        <Flex w="100%" justifyContent="space-between" alignItems="center">
            <PanelTitle>
                {`${getUniversalSearchLabel(dexNextEnabled)}`} Results ({dexResultsStore.resultCount})
            </PanelTitle>
            <DexResultsMoreActionsMenu />
        </Flex>
    );
});

type DexResultsPanelParams = {
    panel: WorkspacePanel;
};
const WrappedPanel = observer(
    ({ panel }: DexResultsPanelParams): React.ReactElement => {
        const leftPanelListContainerRef = useRef<HTMLDivElement | null>(null);
        const rightPanelScrollContainerRef = useRef<HTMLDivElement | null>(null);
        const { dexResultsStore } = useDexResults();
        const { selectDexResult, selectDexResultsSummary } = useSelectDexResults(dexResultsStore);

        const onKeyboardSelectsResult = useCallback(
            ({ summaryId, resultId }: { summaryId?: number; resultId?: number }) => {
                if (summaryId) {
                    selectDexResultsSummary(summaryId);
                } else if (resultId) {
                    selectDexResult(resultId);
                }
            },
            [selectDexResult, selectDexResultsSummary]
        );
        const { keyDownHandler } = useDexResultsKeyboardNavigationAndScroll(
            leftPanelListContainerRef,
            rightPanelScrollContainerRef,
            dexResultsStore,
            onKeyboardSelectsResult
        );

        return (
            <Panel
                testId="DEX_RESULTS_PANEL"
                onKeyDown={keyDownHandler}
                tabIndex={-1}
                panel={panel}
                header={<DexResultsPanelHeader />}
                body={
                    <DexResultsPanelBody
                        leftPanelListContainerRef={leftPanelListContainerRef}
                        rightPanelScrollContainerRef={rightPanelScrollContainerRef}
                    />
                }
            />
        );
    }
);

export function DexResultsPanel({ panel }: { panel: WorkspacePanel }): React.ReactElement {
    const { dexNextEnabled } = useEnabledFeatures();
    return (
        <ErrorBoundary name="PANEL" messageVerbNoun="opening the dex results panel" asToast={true}>
            {dexNextEnabled ? (
                <DexNextResultsPanel panel={panel} />
            ) : (
                <WrappedPanel panel={panel} />
            )}
        </ErrorBoundary>
    );
}
